import { CrossMarketStatus, KrMarketStatus, SafetyStatus, UsMarketStatus } from '../../services/crossMarketApi';

interface Props {
  scope: 'ALL' | 'US' | 'KR';
  us: UsMarketStatus;
  kr: KrMarketStatus;
  safety: SafetyStatus;
}

export default function SystemMarketCards({ scope, us, kr, safety }: Props) {
  return (
    <section className="market-card-grid">
      {scope !== 'KR' && <UsCard data={us} safety={safety} />}
      {scope !== 'US' && <KrCard data={kr} />}
    </section>
  );
}

function UsCard({ data, safety }: { data: UsMarketStatus; safety: SafetyStatus }) {
  const live = isUsLive(safety);
  const watchlist = data.live_watchlist;
  const ready = watchlist?.ready;
  return (
    <article className="market-card us-market">
      <MarketHead market="US" title="미국 시장" mode={live ? 'Toss US Live Pilot' : 'Toss US 후보 검증 · 주문 잠금'} />
      <p className="market-description">
        {live
          ? 'Toss 검증 universe 안에서 live 전략 신호와 risk gate를 통과할 때만 주문합니다.'
          : 'LIVE 후보 universe는 Toss 검증 기준으로 유지하고, 주문 switch는 잠금 상태입니다.'}
      </p>
      <div className="market-metrics">
        <Metric label="LIVE 후보" value={`${watchlist?.count || 0}개`} />
        <Metric label="검증 상태" value={ready ? '통과' : '미검증'} />
        <Metric label="주문 switch" value={live ? 'ON' : '잠금'} />
        <Metric label="세션" value={data.latest_session || '데이터 없음'} />
      </div>
      <Evidence label="Toss LIVE universe" value={watchlistLabel(watchlist)} />
      <Evidence label="후보 source" value={watchlist?.source || '데이터 없음'} />
      <Evidence label="최근 검증 참고" value={formatDate(watchlist?.updated_at || data.latest_observed_at)} />
      <Evidence label="최근 관측 참고" value={data.symbols.join(', ') || '데이터 없음'} />
      <Stage
        status={live ? 'US LIVE pilot 활성' : 'US LIVE 주문 잠금'}
        text={`${watchlist?.source || 'source 없음'} · ${ready ? 'Toss 검증 완료' : 'Toss 검증 필요'}`}
        warn={!ready}
      />
    </article>
  );
}

function KrCard({ data }: { data: KrMarketStatus }) {
  const liveOrders = data.live_summary?.orders.count ?? 0;
  const holdings = data.live_summary?.holdings.count ?? 0;
  const shadowCount = data.live_shadow?.recent_count ?? 0;
  return (
    <article className="market-card kr-market">
      <MarketHead market="KR" title="국내 시장" mode="Toss KR 잠금 · Shadow/Replay" />
      <p className="market-description">KR은 실제 주문 비활성 상태이며, Toss 후보 검증·보유주 ownership·shadow를 관찰합니다.</p>
      <div className="market-metrics">
        <Metric label="LIVE 주문" value={`${liveOrders}건`} />
        <Metric label="보유 종목" value={`${holdings}개`} />
        <Metric label="Shadow" value={`${shadowCount}건`} />
        <Metric label="Replay Run" value={`${data.run_count}개`} />
      </div>
      <Evidence label="전략 기준선" value={data.strategy_profile || '데이터 없음'} />
      <Evidence label="비용 모델" value={data.cost_model_version || '데이터 없음'} />
      <Evidence label="최근 Run" value={shortId(data.latest_run_id)} />
      <KrReadiness data={data} />
    </article>
  );
}

function KrReadiness({ data }: { data: KrMarketStatus }) {
  const readiness = data.live_readiness;
  if (!readiness) {
    return <Stage status="기술 기준선 완료" text="성과 검증 K2는 아직 통과하지 않음" warn />;
  }
  const blocked = readiness.blockers.length > 0;
  return (
    <>
      <Evidence
        label="LIVE 후보"
        value={`${readiness.watchlist.source} · ${readiness.watchlist.count}개 · 이름 ${readiness.watchlist.display_name_count}개`}
      />
      <Evidence
        label="기존 보유주"
        value={`관찰 ${readiness.ownership.external_observed} · 편입 ${readiness.ownership.managed_existing} · 전략 ${readiness.ownership.strategy_managed}`}
      />
      <Stage
        status={blocked ? 'KR LIVE 차단' : 'KR LIVE 준비 잠금'}
        text={blocked ? readiness.blockers.join(', ') : '승인 전 주문 스위치 잠금'}
        warn={blocked}
      />
    </>
  );
}

function MarketHead({ market, title, mode }: { market: string; title: string; mode: string }) {
  return <header className="market-head"><span>{market}</span><div><h2>{title}</h2><small>{mode}</small></div></header>;
}
function Metric({ label, value }: { label: string; value: string }) {
  return <span><small>{label}</small><strong>{value}</strong></span>;
}
function Evidence({ label, value }: { label: string; value: string }) {
  return <div className="market-evidence"><small>{label}</small><span>{value}</span></div>;
}
function Stage({ status, text, warn = false }: { status: string; text: string; warn?: boolean }) {
  return <div className={`market-stage${warn ? ' warn' : ''}`}><strong>{status}</strong><span>{text}</span></div>;
}
function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('ko-KR') : '데이터 없음';
}
function shortId(value: string | null) {
  return value ? `${value.slice(0, 8)}…` : '데이터 없음';
}

function isUsLive(safety: SafetyStatus) {
  return safety.trading_mode === 'live'
    && safety.live_trading_enabled
    && safety.toss_live_market === 'US'
    && safety.toss_us_live_enabled;
}

function watchlistLabel(watchlist: CrossMarketStatus['markets']['US']['live_watchlist']) {
  if (!watchlist) return '데이터 없음';
  const sample = watchlist.tickers.slice(0, 10).join(', ');
  const more = watchlist.count > 10 ? ` 외 ${watchlist.count - 10}` : '';
  return sample ? `${sample}${more}` : '데이터 없음';
}
